import { ref, type Ref, computed } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';

function useDriverDebugDatePicker(range: { start: Ref<Date | null>; end: Ref<Date | null> }) {
	const { t } = useI18n();

	// ── DOM ref ──────────────────────────────────────────────────
	const datePickerRef = ref<HTMLElement | null>(null);

	// ── Picker open state + dropdown position ────────────────────
	const isPickerOpen = ref(false);

	// ── Calendar navigation ──────────────────────────────────────
	const startYear = ref(new Date().getFullYear());
	const startMonth = ref(new Date().getMonth());
	const endYear = ref(new Date().getFullYear());
	const endMonth = ref(new Date().getMonth() < 11 ? new Date().getMonth() + 1 : 0);

	// ── Selection state ──────────────────────────────────────────
	const tempStart = ref<Date | null>(null);
	const tempEnd = ref<Date | null>(null);
	const { start: appliedStartDate, end: appliedEndDate } = range;

	const canApply = computed(() => tempStart.value !== null && tempEnd.value !== null);

	const formattedRange = computed(() => {
		if (!appliedStartDate.value || !appliedEndDate.value) return t('date_picker_range_placeholder');
		const fmt = (d: Date) => d.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
		return `${fmt(appliedStartDate.value)} → ${fmt(appliedEndDate.value)}`;
	});

	// ── Helpers ──────────────────────────────────────────────────
	function syncEndFromStart() {
		const next = new Date(startYear.value, startMonth.value + 1, 1);
		endYear.value = next.getFullYear();
		endMonth.value = next.getMonth();
	}


	function clearDates() {
		tempStart.value = null;
		tempEnd.value = null;
		appliedStartDate.value = null;
		appliedEndDate.value = null;
		isPickerOpen.value = false;
		const now = new Date();
		startYear.value = now.getFullYear();
		startMonth.value = now.getMonth();
		syncEndFromStart();
	}

	function restoreFromStorage() {
		let storedStart = localStorage.getItem('debugStartDate');
		let storedEnd = localStorage.getItem('debugEndDate');
		if (!storedStart) {
			storedStart = String(DateTime.now().minus({ months: 6 }).toMillis());
			localStorage.setItem('debugStartDate', storedStart);
		}
		if (!storedEnd) {
			storedEnd = String(DateTime.now().plus({ months: 6 }).toMillis());
			localStorage.setItem('debugEndDate', storedEnd);
		}
		appliedStartDate.value = new Date(Number(storedStart));
		appliedEndDate.value = new Date(Number(storedEnd));
		startYear.value = appliedStartDate.value.getFullYear();
		startMonth.value = appliedStartDate.value.getMonth();
		endYear.value = appliedEndDate.value.getFullYear();
		endMonth.value = appliedEndDate.value.getMonth();
	}

	return {
		// refs
		datePickerRef,
		isPickerOpen,
		startYear,
		startMonth,
		endYear,
		endMonth,
		tempStart,
		tempEnd,
		appliedStartDate,
		appliedEndDate,
		// computed
		canApply,
		formattedRange,
		// methods
		clearDates,
		restoreFromStorage,
	};
}

export default useDriverDebugDatePicker;
